import {
  createApi,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import {
  resolveAggregatorSegment,
  type AggregatorCredentialsModel,
} from '@/utils/aggregatorCredentials';
import { aggregatorQuery } from './baseQueries';

export interface AggregatorCredentialsArgs {
  aggregatorId: number;
  wealthId: number;
}

export interface UpdateAggregatorCredentialsArgs {
  aggregatorId: number;
  model: AggregatorCredentialsModel;
}

const unsupportedAggregator = (aggregatorId: number): FetchBaseQueryError => {
  const message = `No generic credentials flow for aggregator ${String(aggregatorId)}`;
  return { status: 'CUSTOM_ERROR', data: { message }, error: message };
};

/**
 * Generic aggregator credentials, shared by every aggregator that speaks the
 * declarative form contract (ByAll, Demo). The aggregator only determines the URL
 * segment — exactly how legacy built it in `SetupCredentials.jsx:40-51`.
 *
 * Yodlee is deliberately absent: it reconnects through the FastLink iframe and has
 * its own slice.
 */
export const aggregatorCredentialsApi = createApi({
  reducerPath: 'aggregatorCredentialsApi',
  baseQuery: aggregatorQuery,
  tagTypes: ['AggregatorCredentials'],
  // A credentials form is a one-shot challenge: MFA questions and captchas go
  // stale immediately, so never serve a cached one when the modal reopens.
  keepUnusedDataFor: 0,
  endpoints: (builder) => ({
    getAggregatorCredentials: builder.query<
      AggregatorCredentialsModel,
      AggregatorCredentialsArgs
    >({
      queryFn: async ({ aggregatorId, wealthId }, _api, __, baseQuery) => {
        const segment = resolveAggregatorSegment(aggregatorId);
        if (!segment) {
          return { error: unsupportedAggregator(aggregatorId) };
        }

        const result = await baseQuery({
          url: `/api/${segment}/credentials/${String(wealthId)}`,
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data as AggregatorCredentialsModel };
      },
      providesTags: (_result, _error, { wealthId }) => [
        { type: 'AggregatorCredentials', id: wealthId },
      ],
    }),
    updateAggregatorCredentials: builder.mutation<
      AggregatorCredentialsModel,
      UpdateAggregatorCredentialsArgs
    >({
      queryFn: async ({ aggregatorId, model }, _api, __, baseQuery) => {
        const segment = resolveAggregatorSegment(aggregatorId);
        if (!segment) {
          return { error: unsupportedAggregator(aggregatorId) };
        }

        // The whole model goes back, with each field's Value filled in. The
        // wealth id travels in the body, never in the path.
        const result = await baseQuery({
          url: `/api/${segment}/credentials`,
          method: 'PUT',
          body: model,
        });

        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data as AggregatorCredentialsModel };
      },
    }),
  }),
});

export const {
  useGetAggregatorCredentialsQuery,
  useLazyGetAggregatorCredentialsQuery,
  useUpdateAggregatorCredentialsMutation,
} = aggregatorCredentialsApi;
